import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Check, X, Zap, Sparkles, Crown, Gift,
  ArrowRight, HelpCircle, ChevronDown,
  Banknote, Loader2, ArrowUpRight, ArrowDownRight,
  CheckCircle, AlertTriangle, Tag
} from 'lucide-react';
import { useAuth } from '../../components/auth/AuthContext';
import { stripeService } from '../../services/StripeService';
import Logo from '../../components/home/Logo';
import EmbeddedCheckoutModal from '../../components/checkout/EmbeddedCheckoutModal';

const PlansPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);
  
  // Estados para usuário logado
  const [currentPlan, setCurrentPlan] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [currentBillingInterval, setCurrentBillingInterval] = useState(null);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(null);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);
  const [error, setError] = useState(null);

  // 🆕 Toggle mensal/anual — default ANUAL para engajar
  const [billingInterval, setBillingInterval] = useState('yearly');

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Carregar plano atual se usuário estiver logado
  useEffect(() => {
    if (currentUser) {
      loadCurrentPlan();
    }
  }, [currentUser]);

  // ✅ FIX: Limpar isLoadingCheckout quando o usuário voltar para a página
  // (ex: fechou a aba do Stripe e voltou, ou usou botão "Voltar" do navegador)
  useEffect(() => {
    const handlePageShow = (event) => {
      // pageshow é disparado quando a página é restaurada do bfcache
      if (event.persisted) {
        setIsLoadingCheckout(null);
      }
    };

    const handleVisibilityChange = () => {
      // Se a página voltou a ficar visível e estava em loading, resetar
      if (document.visibilityState === 'visible' && isLoadingCheckout) {
        setIsLoadingCheckout(null);
      }
    };

    const handleFocus = () => {
      // Quando a janela recebe foco novamente (voltou do Stripe)
      if (isLoadingCheckout) {
        // Pequeno delay para garantir que o redirect não está em andamento
        setTimeout(() => {
          setIsLoadingCheckout(null);
        }, 1000);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isLoadingCheckout]);

  const loadCurrentPlan = async () => {
    setIsLoadingPlan(true);
    try {
      const response = await stripeService.getSubscriptionStatus();
      if (response.success && response.data) {
        setCurrentPlan(response.data.plan || 'free');
        setSubscriptionStatus(response.data.status);
        setCurrentBillingInterval(response.data.billing_interval || 'monthly');
      }
    } catch (err) {
      console.warn('Erro ao carregar plano atual:', err);
      setCurrentPlan('free');
    } finally {
      setIsLoadingPlan(false);
    }
  };

  // Dados dos planos — modelo de CRÉDITOS
  // 1 crédito = 1 imagem · 5 créditos = 1 vídeo
  const plans = [
    {
      id: 'free',
      name: 'Grátis',
      description: 'Experimente sem cartão',
      icon: Gift,
      color: 'gray',
      popular: false,
      order: 0,
      free: true,
      features: [
        { text: '2 créditos por mês', included: true },
        { text: '2 imagens por IA/mês', included: true },
        { text: 'Templates básicos', included: true },
        { text: 'Sem cartão de crédito', included: true },
        { text: 'Vídeos por IA (Reels)', included: false },
        { text: 'Publicação automática', included: false },
      ]
    },
    {
      id: 'basic',
      name: 'Básico',
      description: 'Ideal para quem está começando',
      icon: Zap,
      color: 'blue',
      popular: false,
      order: 1,
      features: [
        { text: '120 créditos por mês', included: true },
        { text: '1 crédito = 1 imagem · 5 = 1 vídeo', included: true },
        { text: 'Geração de posts por IA', included: true },
        { text: 'Vídeos animados por IA (Reels)', included: true },
        { text: 'Legendas automáticas', included: true },
        { text: 'Publicação automática', included: true },
        { text: 'Biblioteca premium', included: false },
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      description: 'Perfeito para empresas',
      icon: Sparkles,
      color: 'amber',
      popular: true,
      order: 2,
      features: [
        { text: 'Tudo do Básico', included: true },
        { text: '270 créditos por mês', included: true },
        { text: '1 crédito = 1 imagem · 5 = 1 vídeo', included: true },
        { text: 'Templates avançados', included: true },
        { text: 'Biblioteca de conteúdos', included: true },
        { text: 'Qualidade HD', included: true },
        { text: 'Sons nos posts', included: false },
        { text: 'Qualidade 4K', included: false },
      ]
    },
    {
      id: 'max',
      name: 'Max',
      description: 'Para quem quer tudo',
      icon: Crown,
      color: 'purple',
      popular: false,
      order: 3,
      features: [
        { text: 'Tudo do Pro', included: true },
        { text: '550 créditos por mês', included: true },
        { text: '1 crédito = 1 imagem · 5 = 1 vídeo', included: true },
        { text: 'Sons nos posts', included: true },
        { text: 'Biblioteca premium de vídeos', included: true },
        { text: 'Prioridade de geração', included: true },
        { text: 'Qualidade HD + 4K', included: true },
        { text: 'Suporte prioritário', included: true },
      ]
    }
  ];

  // Ordem dos planos para comparação
  const planOrder = { free: 0, basic: 1, pro: 2, max: 3 };

  // Verificar tipo de ação
  const getPlanAction = (planId) => {
    // Card GRÁTIS: cadastro sem cartão (deslogado) / plano atual (free) /
    // indisponível como "downgrade" direto (assinante cancela pela conta)
    if (planId === 'free') {
      if (!currentUser) return 'subscribe';
      if (!currentPlan || currentPlan === 'free') return 'current';
      return 'free_unavailable';
    }
    if (!currentPlan || currentPlan === 'free') return 'subscribe';
    if (planId === currentPlan) return 'current';
    if (planOrder[planId] > planOrder[currentPlan]) return 'upgrade';
    return 'downgrade';
  };

  // Preço exibido baseado no toggle
  const getDisplayPrice = (planId) => {
    const plan = stripeService.helpers.plans[planId];
    if (!plan) return 0;
    if (billingInterval === 'yearly') {
      return plan.yearlyPrice / 12; // Mostra equivalente mensal
    }
    return plan.monthlyPrice;
  };

  // Preço total anual
  const getYearlyTotal = (planId) => {
    const plan = stripeService.helpers.plans[planId];
    return plan?.yearlyPrice || 0;
  };

  // Dados da comparação de economia
  const savingsComparison = [
    { name: 'Agência de Marketing', price: 'R$ 2000', highlight: false },
    { name: 'Designer', price: 'R$ 1500', highlight: false },
    { name: 'Editor Mágico hello, social', price: 'Apenas R$39,90', highlight: true },
  ];

  // FAQ
  const faqItems = [
    {
      question: 'Posso cancelar a qualquer momento?',
      answer: 'Sim! Você pode cancelar sua assinatura a qualquer momento. Você continuará tendo acesso até o final do período pago.'
    },
    {
      question: 'Como funcionam os créditos?',
      answer: 'Cada geração consome créditos: 1 crédito por imagem e 5 créditos por vídeo. No plano gratuito você recebe 2 créditos por mês (2 imagens). Nos planos pagos, os créditos renovam todo mês automaticamente.'
    },
    {
      question: 'Posso trocar de plano depois?',
      answer: 'Sim! Você pode fazer upgrade ou downgrade do seu plano a qualquer momento. A diferença será calculada proporcionalmente.'
    },
    {
      question: 'Qual a diferença entre mensal e anual?',
      answer: 'O plano anual oferece até 18% de desconto em relação ao mensal. Você paga uma vez por ano e tem acesso a todos os recursos do plano escolhido. Os limites de uso (imagens e vídeos) continuam sendo renovados mensalmente.'
    },
    {
      question: 'Quais formas de pagamento são aceitas?',
      answer: 'Aceitamos todos os cartões de crédito (Visa, Mastercard, American Express, Elo) através do Stripe, a plataforma de pagamentos mais segura do mundo.'
    },
    {
      question: 'O que acontece se eu atingir o limite?',
      answer: 'Você receberá um aviso quando estiver próximo do limite. Ao atingir, você pode fazer upgrade do plano ou aguardar a renovação mensal.'
    }
  ];

  // ✅ FIX: Handler robusto para selecionar plano
  const handleSelectPlan = async (planId) => {
    setError(null);

    // GRÁTIS: nunca abre checkout — deslogado vai pro cadastro sem cartão
    if (planId === 'free') {
      if (!currentUser) navigate('/register?plan=free');
      return;
    }

    if (currentUser) {
      const action = getPlanAction(planId);

      if (action === 'current' || action === 'free_unavailable') return;
      
      // ✅ FIX: Se já está em loading para esse plano, não fazer nada (evitar duplo clique)
      if (isLoadingCheckout === planId) return;
      
      setIsLoadingCheckout(planId);

      try {
        const response = await stripeService.createCheckoutSession(planId, billingInterval);

        if (response.success && response.data?.client_secret) {
          setCheckoutClientSecret(response.data.client_secret);
        } else {
          throw new Error('Não foi possível criar a sessão de checkout. Tente novamente.');
        }
      } catch (err) {
        console.error('Erro ao iniciar checkout:', err);

        const errorMsg = err.response?.data?.detail
          || err.message
          || 'Erro ao iniciar processo de checkout. Por favor, tente novamente.';

        setError(errorMsg);
      } finally {
        setIsLoadingCheckout(null);
      }
    } else {
      navigate(`/register?plan=${planId}&interval=${billingInterval}`);
    }
  };

  // Obter texto e estilo do botão baseado na ação
  const getButtonConfig = (planId, colors) => {
    const action = getPlanAction(planId);
    const isLoading = isLoadingCheckout === planId;
    
    switch (action) {
      case 'current':
        return {
          text: 'Plano Atual',
          icon: CheckCircle,
          className: 'bg-gray-200 text-gray-600 cursor-not-allowed',
          disabled: true
        };
      case 'free_unavailable':
        return {
          text: 'Cancele pela sua conta',
          icon: CheckCircle,
          className: 'bg-gray-100 text-gray-400 cursor-not-allowed',
          disabled: true
        };
      case 'upgrade':
        return {
          text: isLoading ? 'Processando...' : 'Fazer Upgrade',
          icon: isLoading ? Loader2 : ArrowUpRight,
          className: `${colors.button} text-white`,
          disabled: isLoading
        };
      case 'downgrade':
        return {
          text: isLoading ? 'Processando...' : 'Fazer Downgrade',
          icon: isLoading ? Loader2 : ArrowDownRight,
          className: 'bg-gray-600 hover:bg-gray-700 text-white',
          disabled: isLoading
        };
      default: // subscribe
        return {
          text: isLoading ? 'Processando...' : `Começar com ${plans.find(p => p.id === planId)?.name}`,
          icon: isLoading ? Loader2 : ArrowRight,
          className: `${colors.button} text-white`,
          disabled: isLoading
        };
    }
  };

  // Cores por plano
  const getColorClasses = (color, popular) => {
    const colors = {
      gray: {
        gradient: 'from-gray-400 to-gray-500',
        bg: 'bg-gray-50',
        border: 'border-gray-200',
        text: 'text-gray-600',
        button: 'bg-gray-800 hover:bg-gray-900',
        light: 'bg-gray-100',
        ring: 'ring-gray-400'
      },
      blue: {
        gradient: 'from-blue-500 to-cyan-500',
        bg: 'bg-blue-50',
        border: popular ? 'border-blue-500' : 'border-gray-200',
        text: 'text-blue-600',
        button: 'bg-blue-600 hover:bg-blue-700',
        light: 'bg-blue-100',
        ring: 'ring-blue-500'
      },
      amber: {
        gradient: 'from-amber-400 to-orange-500',
        bg: 'bg-amber-50',
        border: popular ? 'border-amber-500' : 'border-gray-200',
        text: 'text-amber-600',
        button: 'bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600',
        light: 'bg-amber-100',
        ring: 'ring-amber-500'
      },
      purple: {
        gradient: 'from-purple-500 to-pink-500',
        bg: 'bg-purple-50',
        border: popular ? 'border-purple-500' : 'border-gray-200',
        text: 'text-purple-600',
        button: 'bg-purple-600 hover:bg-purple-700',
        light: 'bg-purple-100',
        ring: 'ring-purple-500'
      }
    };
    return colors[color];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/">
            <Logo className="h-10" showSocial={true} />
          </Link>
          <div className="flex items-center gap-4">
            {currentUser ? (
              <Link
                to="/account"
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Minha Conta
              </Link>
            ) : (
              <Link
                to="/login"
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Entrar
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Mensagem de erro */}
      {error && (
        <div className="max-w-6xl mx-auto px-4 mb-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <button 
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* ✅ Banner informativo para usuário FREE que precisa assinar */}
      {currentUser && currentPlan === 'free' && (
        <div className="max-w-6xl mx-auto px-4 mb-6">
          <div className="bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 text-white rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <p className="font-bold text-lg">Escolha um plano para começar!</p>
                <p className="text-white/90 text-sm">
                  Selecione o plano ideal para acessar todas as funcionalidades da plataforma.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner para usuário logado com plano atual */}
      {currentUser && currentPlan && currentPlan !== 'free' && (
        <div className="max-w-6xl mx-auto px-4 mb-6">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6" />
              </div>
              <div>
                <p className="text-white/80 text-sm">Seu plano atual</p>
                <p className="text-xl font-bold capitalize">
                  {currentPlan}
                  {currentBillingInterval && (
                    <span className="text-sm font-normal ml-2 text-white/70">
                      ({stripeService.helpers.formatBillingInterval(currentBillingInterval)})
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white/80 text-sm">
                Gerencie sua assinatura no Stripe
              </span>
              <button
                onClick={() => stripeService.redirectToPortal()}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl font-medium transition-all"
              >
                Gerenciar Assinatura
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="py-12 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4" />
            Comece grátis: 2 imagens por mês, sem cartão
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {currentUser && currentPlan && currentPlan !== 'free' 
              ? 'Altere seu plano' 
              : 'Escolha o plano ideal para você'}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {currentUser && currentPlan && currentPlan !== 'free'
              ? 'Faça upgrade ou downgrade a qualquer momento. A diferença será calculada proporcionalmente.'
              : 'Comece grátis e faça upgrade quando precisar. Cancele a qualquer momento.'}
          </p>

          {/* 🆕 Toggle Mensal/Anual */}
          <div className="inline-flex items-center gap-1 p-1.5 bg-gray-100 rounded-2xl">
            <button
              onClick={() => setBillingInterval('monthly')}
              className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all ${
                billingInterval === 'monthly'
                  ? 'bg-white text-gray-900 shadow-md'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setBillingInterval('yearly')}
              className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all relative ${
                billingInterval === 'yearly'
                  ? 'bg-white text-gray-900 shadow-md'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Anual
              <span className="absolute -top-3 -right-3 px-2 py-0.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[10px] font-bold rounded-full shadow-sm">
                -18%
              </span>
            </button>
          </div>

          {/* Mensagem de economia */}
          {billingInterval === 'yearly' && (
            <p className="mt-4 text-sm text-green-700 font-medium flex items-center justify-center gap-1.5">
              <Tag className="w-4 h-4" />
              Economize até {stripeService.helpers.formatCurrency(stripeService.helpers.getYearlySavings('pro'))} por ano no plano Pro!
            </p>
          )}
        </div>
      </section>

      {/* Plans Grid */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {isLoadingPlan ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              <span className="ml-3 text-gray-600">Carregando planos...</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 items-stretch">
              {plans.map((plan) => {
                const Icon = plan.icon;
                const colors = getColorClasses(plan.color, plan.popular);
                const action = getPlanAction(plan.id);
                const buttonConfig = getButtonConfig(plan.id, colors);
                const ButtonIcon = buttonConfig.icon;
                const isCurrentPlan = action === 'current';
                
                const displayPrice = getDisplayPrice(plan.id);
                const discount = stripeService.helpers.getYearlyDiscount(plan.id);
                const monthlyPrice = stripeService.helpers.plans[plan.id]?.monthlyPrice || 0;
                
                return (
                  <div
                    key={plan.id}
                    className={`relative bg-white rounded-3xl border-2 flex flex-col ${
                      isCurrentPlan
                        ? `${colors.border} ring-2 ${colors.ring} ring-offset-2`
                        : colors.border
                    } p-6 transition-all hover:shadow-xl ${
                      plan.popular && !isCurrentPlan ? 'shadow-lg xl:scale-[1.03] z-10' : 'hover:scale-[1.01]'
                    }`}
                  >
                    {/* Badge Popular ou Plano Atual */}
                    {isCurrentPlan ? (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <span className="inline-flex items-center gap-1 px-4 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm font-bold rounded-full shadow-lg">
                          <CheckCircle className="w-4 h-4" />
                          SEU PLANO
                        </span>
                      </div>
                    ) : plan.popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <span className={`inline-flex items-center gap-1 px-4 py-1.5 bg-gradient-to-r ${colors.gradient} text-white text-sm font-bold rounded-full shadow-lg`}>
                          <Sparkles className="w-4 h-4" />
                          MAIS VENDIDO
                        </span>
                      </div>
                    )}

                    {/* Header do Plano */}
                    <div className="text-center mb-6">
                      <div className={`inline-flex items-center justify-center w-16 h-16 ${colors.light} rounded-2xl mb-4`}>
                        <Icon className={`w-8 h-8 ${colors.text}`} />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                      <p className="text-gray-500 text-sm">{plan.description}</p>
                    </div>

                    {/* Preço */}
                    <div className="text-center mb-6">
                      {/* Preço riscado se anual */}
                      {billingInterval === 'yearly' && (
                        <p className="text-gray-400 line-through text-lg mb-1">
                          R$ {monthlyPrice.toFixed(2)}/mês
                        </p>
                      )}
                      
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-lg text-gray-500">R$</span>
                        <span className="text-4xl font-bold text-gray-900">
                          {displayPrice.toFixed(2).split('.')[0]}
                        </span>
                        <span className="text-xl text-gray-500">
                          ,{displayPrice.toFixed(2).split('.')[1]}
                        </span>
                      </div>
                      <p className="text-gray-500 text-sm mt-1">/mês</p>

                      {/* Info de cobrança anual */}
                      {billingInterval === 'yearly' && (
                        <div className="mt-3 space-y-1">
                          <p className="text-xs text-gray-500">
                            Cobrado {stripeService.helpers.formatCurrency(getYearlyTotal(plan.id))}/ano
                          </p>
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full">
                            <Tag className="w-3 h-3" />
                            Economia de {discount}%
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Features (flex-1 ancora o CTA embaixo em todos os cards) */}
                    <ul className="space-y-2.5 mb-6 flex-1 text-sm">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          {feature.included ? (
                            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <button
                      onClick={() => handleSelectPlan(plan.id)}
                      disabled={buttonConfig.disabled}
                      className={`w-full py-3 px-5 ${buttonConfig.className} font-semibold rounded-xl transition-all transform ${
                        !buttonConfig.disabled ? 'hover:scale-[1.02]' : ''
                      } shadow-lg flex items-center justify-center gap-2 disabled:transform-none`}
                    >
                      {buttonConfig.text}
                      <ButtonIcon className={`w-5 h-5 ${isLoadingCheckout === plan.id ? 'animate-spin' : ''}`} />
                    </button>

                    {/* Trial info */}
                    {!isCurrentPlan && (
                      <p className="text-center text-sm text-gray-500 mt-4">
                        {action === 'upgrade' || action === 'downgrade' 
                          ? 'Alteração imediata • Valor proporcional'
                          : 'Créditos liberados na hora • Cancele quando quiser'}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Seção de Economia */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Você vai economizar dinheiro
            </h2>
            <p className="text-lg text-gray-600 flex items-center justify-center gap-2">
              Ter um perfil profissional no Instagram não precisa ser caro
              <span className="text-2xl">💸</span>
            </p>
          </div>

          <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100">
            {savingsComparison.map((item, idx) => (
              <div
                key={idx}
                className={`flex items-center justify-between px-8 md:px-12 py-5 ${
                  item.highlight 
                    ? 'bg-gradient-to-r from-amber-100 to-orange-100' 
                    : idx !== savingsComparison.length - 1 
                      ? 'border-b border-gray-100' 
                      : ''
                }`}
              >
                <span className={`text-lg ${item.highlight ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                  {item.name}
                </span>
                <span className={`text-lg ${
                  item.highlight 
                    ? 'font-bold text-orange-600' 
                    : 'font-medium text-gray-900'
                }`}>
                  {item.price}
                </span>
              </div>
            ))}
          </div>

          {/* CTA após economia */}
          {(!currentUser || currentPlan === 'free') && (
            <div className="text-center mt-8">
              <button
                onClick={() => handleSelectPlan('basic')}
                disabled={isLoadingCheckout === 'basic'}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold rounded-xl hover:from-amber-500 hover:to-orange-600 transition-all transform hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:transform-none"
              >
                {isLoadingCheckout === 'basic' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Banknote className="w-5 h-5" />
                    Começar a economizar agora
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Comparativo de Recursos */}
      <section className="py-16 px-4 bg-white/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Compare os planos
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-4 font-semibold text-gray-900">Recurso</th>
                  <th className={`text-center py-4 px-4 font-semibold ${currentPlan === 'basic' ? 'text-blue-600 bg-blue-50 rounded-t-xl' : 'text-blue-600'}`}>
                    Básico {currentPlan === 'basic' && '✓'}
                  </th>
                  <th className={`text-center py-4 px-4 font-semibold ${currentPlan === 'pro' ? 'text-amber-600 bg-amber-50 rounded-t-xl' : 'text-amber-600'}`}>
                    Pro {currentPlan === 'pro' && '✓'}
                  </th>
                  <th className={`text-center py-4 px-4 font-semibold ${currentPlan === 'max' ? 'text-purple-600 bg-purple-50 rounded-t-xl' : 'text-purple-600'}`}>
                    Max {currentPlan === 'max' && '✓'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="py-4 px-4 text-gray-700">Preço mensal</td>
                  <td className={`py-4 px-4 text-center font-medium ${currentPlan === 'basic' ? 'bg-blue-50' : ''}`}>
                    {billingInterval === 'yearly' 
                      ? <><span className="line-through text-gray-400 text-xs">R$ 39,90</span><br />R$ {(stripeService.helpers.plans.basic.yearlyPrice / 12).toFixed(2)}</>
                      : 'R$ 39,90'}
                  </td>
                  <td className={`py-4 px-4 text-center font-medium ${currentPlan === 'pro' ? 'bg-amber-50' : ''}`}>
                    {billingInterval === 'yearly' 
                      ? <><span className="line-through text-gray-400 text-xs">R$ 79,90</span><br />R$ {(stripeService.helpers.plans.pro.yearlyPrice / 12).toFixed(2)}</>
                      : 'R$ 79,90'}
                  </td>
                  <td className={`py-4 px-4 text-center font-medium ${currentPlan === 'max' ? 'bg-purple-50' : ''}`}>
                    {billingInterval === 'yearly' 
                      ? <><span className="line-through text-gray-400 text-xs">R$ 149,90</span><br />R$ {(stripeService.helpers.plans.max.yearlyPrice / 12).toFixed(2)}</>
                      : 'R$ 149,90'}
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-gray-700">Posts por IA/mês</td>
                  <td className={`py-4 px-4 text-center font-medium ${currentPlan === 'basic' ? 'bg-blue-50' : ''}`}>120</td>
                  <td className={`py-4 px-4 text-center font-medium ${currentPlan === 'pro' ? 'bg-amber-50' : ''}`}>250</td>
                  <td className={`py-4 px-4 text-center font-medium ${currentPlan === 'max' ? 'bg-purple-50' : ''}`}>500</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-gray-700">Vídeos por IA/mês</td>
                  <td className={`py-4 px-4 text-center text-gray-400 ${currentPlan === 'basic' ? 'bg-blue-50' : ''}`}>-</td>
                  <td className={`py-4 px-4 text-center font-medium ${currentPlan === 'pro' ? 'bg-amber-50' : ''}`}>4</td>
                  <td className={`py-4 px-4 text-center font-medium ${currentPlan === 'max' ? 'bg-purple-50' : ''}`}>10</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-gray-700">Templates</td>
                  <td className={`py-4 px-4 text-center ${currentPlan === 'basic' ? 'bg-blue-50' : ''}`}>Básicos</td>
                  <td className={`py-4 px-4 text-center ${currentPlan === 'pro' ? 'bg-amber-50' : ''}`}>Avançados</td>
                  <td className={`py-4 px-4 text-center ${currentPlan === 'max' ? 'bg-purple-50' : ''}`}>Premium</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-gray-700">Qualidade</td>
                  <td className={`py-4 px-4 text-center ${currentPlan === 'basic' ? 'bg-blue-50' : ''}`}>HD</td>
                  <td className={`py-4 px-4 text-center ${currentPlan === 'pro' ? 'bg-amber-50' : ''}`}>HD</td>
                  <td className={`py-4 px-4 text-center ${currentPlan === 'max' ? 'bg-purple-50' : ''}`}>HD + 4K</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-gray-700">Suporte</td>
                  <td className={`py-4 px-4 text-center ${currentPlan === 'basic' ? 'bg-blue-50 rounded-b-xl' : ''}`}>Email</td>
                  <td className={`py-4 px-4 text-center ${currentPlan === 'pro' ? 'bg-amber-50 rounded-b-xl' : ''}`}>Email + Chat</td>
                  <td className={`py-4 px-4 text-center ${currentPlan === 'max' ? 'bg-purple-50 rounded-b-xl' : ''}`}>Prioritário</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center justify-center w-12 h-12 bg-amber-100 rounded-2xl mb-4">
              <HelpCircle className="w-6 h-6 text-amber-600" />
            </span>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Perguntas Frequentes
            </h2>
            <p className="text-gray-600">
              Tire suas dúvidas sobre nossos planos
            </p>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl border-2 border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{item.question}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      openFaq === idx ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      {(!currentUser || currentPlan === 'free') && (
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 rounded-3xl p-12 text-center text-white">
              <h2 className="text-3xl font-bold mb-4">
                Pronto para começar?
              </h2>
              <p className="text-xl opacity-90 mb-8">
                Experimente grátis com 2 imagens por mês. Sem compromisso.
              </p>
              <button
                onClick={() => handleSelectPlan('pro')}
                disabled={isLoadingCheckout === 'pro'}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-orange-600 font-bold rounded-xl hover:bg-gray-100 transition-all transform hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:transform-none"
              >
                {isLoadingCheckout === 'pro' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    Começar agora com Pro
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo className="h-8" showSocial={true} />
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/terms" className="hover:text-gray-700">Termos de Serviço</Link>
            <Link to="/privacy" className="hover:text-gray-700">Política de Privacidade</Link>
          </div>
        </div>
      </footer>

      {checkoutClientSecret && (
        <EmbeddedCheckoutModal
          clientSecret={checkoutClientSecret}
          onClose={() => {
            setCheckoutClientSecret(null);
            setIsLoadingCheckout(null);
          }}
        />
      )}
    </div>
  );
};

export default PlansPage;